import {
	TreatmentPlan,
	TreatmentPlanRequest,
} from "../../domain/treatmentPlan.interface";
import { readPlanSegments } from "../../components/SegmentEditor/segmentEditor.helpers";

/**
 * SIMS-9027 (FR-5, FR-16) — did this save actually move the disenrollment threshold?
 *
 * Mirrors the backend helper of the same name. Stored-value equality is the wrong test in both
 * directions:
 *
 *   unset -> 30    NO change  — 30 was already in force via the runtime fallback
 *   14    -> unset IS a change — the effective value moves from 14 to 30
 *
 * so the comparison is on the EFFECTIVE value. The frontend needs its own copy because it decides
 * whether to show the confirmation at all; the backend re-verifies before writing anything, so this
 * is a UX gate rather than the authority.
 */

/** Mirrors the `|| 30` fallback in resolveDisenrollmentThreshold (backend). */
export const PLATFORM_DEFAULT_DISENROLLMENT_DAYS = 30;

export const effectiveThreshold = (stored: number | null | undefined): number =>
	stored || PLATFORM_DEFAULT_DISENROLLMENT_DAYS;

export interface EffectiveThresholdChange {
	templateName: string;
	/**
	 * SIMS-9041 — the template's stable id, when the plan has one. Sent with the name so the server
	 * can resolve the cohort by identity: a rename invalidates the label, not the id.
	 */
	templateId?: string;
	/**
	 * WHICH threshold moved (FR-8). A segment change writes into that phase of each participant's
	 * snapshot rather than the template default, so someone still in an earlier phase carries the
	 * corrected value for when they reach it (AC4 / FR-18).
	 */
	field: "TEMPLATE_DEFAULT" | "SEGMENT";
	/** Present only for a segment change — segments carry no id, so the range names them. */
	segmentWeekRange?: { start: number; end: number };
	/** 1-based, for the confirmation copy: "segment 2 (weeks 7-12)". */
	segmentNumber?: number;
	previousValue: number;
	nextValue: number;
}

/**
 * The first template whose effective threshold moved, or null when none did.
 *
 * Returns at most ONE change: the confirmation is per propagating save and the epic's v1 scope is
 * the template default (FR-24 gates anything wider). Templates are matched by NAME because they
 * carry no id — the same missing identity SIMS-9041 is about.
 *
 * Returns null when the plan has not opted in (FR-2/FR-16): the option alone changes nothing, and
 * a plan that never set it behaves exactly as it does today (FR-3).
 */
export const findEffectiveThresholdChange = (
	previous: TreatmentPlan,
	next: TreatmentPlanRequest,
): EffectiveThresholdChange | null => {
	if (!next.propagateThresholdToCurrentEnrollments) {
		return null;
	}

	const previousTemplates =
		previous.treatmentConfigOptions?.treatmentConfigTemplates ?? [];
	const nextTemplates =
		next.treatmentConfigOptions?.treatmentConfigTemplates ?? [];

	for (const nextTemplate of nextTemplates) {
		const previousTemplate = previousTemplates.find(
			(t) => t.name === nextTemplate.name,
		);

		// A template that did not exist before this save has no prior value to have moved from,
		// so nothing has changed for an already-enrolled cohort.
		if (!previousTemplate) {
			continue;
		}

		const before = effectiveThreshold(
			previousTemplate.disenrollmentDaysThreshold,
		);
		const after = effectiveThreshold(nextTemplate.disenrollmentDaysThreshold);

		if (before !== after) {
			return {
				templateName: nextTemplate.name,
				templateId: (nextTemplate as { _id?: string })._id,
				field: "TEMPLATE_DEFAULT",
				previousValue: before,
				nextValue: after,
			};
		}
	}

	// Segments are PLAN-level, not per template: they are sent as a top-level field and the backend
	// folds them into treatmentConfig.segments. A segment with no override inherits the default
	// template's threshold, so that is what a participant in that phase is really judged against.
	const defaultTemplate =
		nextTemplates.find((t) => t.default) ?? nextTemplates[0];
	const previousDefaultTemplate =
		previousTemplates.find((t) => t.default) ?? previousTemplates[0];

	const previousSegments = readPlanSegments(previous.treatmentConfig);
	const nextSegments = next.segments ?? [];

	for (let index = 0; index < nextSegments.length; index += 1) {
		const nextSegment = nextSegments[index];
		const previousSegment = previousSegments.find(
			(s) =>
				s.weekRange?.start === nextSegment.weekRange?.start &&
				s.weekRange?.end === nextSegment.weekRange?.end,
		);

		// A segment added in this save has no prior value to have moved from.
		if (!previousSegment) {
			continue;
		}

		const before = effectiveThreshold(
			previousSegment.disenrollmentDaysThreshold ??
				previousDefaultTemplate?.disenrollmentDaysThreshold,
		);
		const after = effectiveThreshold(
			nextSegment.disenrollmentDaysThreshold ??
				defaultTemplate?.disenrollmentDaysThreshold,
		);

		if (before !== after) {
			return {
				templateName: defaultTemplate?.name ?? "",
				templateId: (defaultTemplate as { _id?: string } | undefined)?._id,
				field: "SEGMENT",
				segmentWeekRange: nextSegment.weekRange,
				segmentNumber: index + 1,
				previousValue: before,
				nextValue: after,
			};
		}
	}

	return null;
};
